import { getAuthToken } from '@efex-services/auth/authUtils';
import { DEFAULT_INTERVAL_VALUE } from '@constants/general';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@efexteam/components';
import { Typography } from '@mui/material';
import JwtParser from '@utils/JwtTokenManager';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useClearStorageService from '@/layout/login/hooks/useClearStorage.service';
import { timeout } from '../../assets';
import { logout } from '../../layout/login/auth';
import { useTokenRefresh } from './hooks';

const RELOAD_RATES = 1800000 / 1000; // 30 minutes
const SHOW_ALERT = 1; // 1 minute

const SessionTimeout = () => {
  const { clearLogoutStorage } = useClearStorageService();
  const jwtParser = useMemo(() => new JwtParser(), []);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [counter, setCounter] = useState(RELOAD_RATES);
  const [openModal, setOpenModal] = useState(false);
  const token = getAuthToken();
  const payload = jwtParser.parse(token);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const { refreshToken } = useTokenRefresh();

  const handleLogout = useCallback(() => {
    clearLogoutStorage();
    dispatch(logout());
  }, [clearLogoutStorage, dispatch]);

  const handleResetSession = async () => {
    try {
      const success = await refreshToken();

      if (success) {
        setCounter(RELOAD_RATES);
        setOpenModal(false);
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    }
  };

  const validateToken = useCallback(() => {
    const isTokenValid = jwtParser.getTimeUntilExpire(payload?.exp || null);
    if (isTokenValid <= SHOW_ALERT) {
      setOpenModal(true);
    } else {
      setOpenModal(false);
    }
    if (isTokenValid <= 0) {
      handleLogout();
    }
  }, [handleLogout, jwtParser, payload?.exp]);

  const handleValidInterval = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
    intervalId.current = setInterval(
      () => setCounter(counter - 1),
      DEFAULT_INTERVAL_VALUE
    );
    validateToken();
  }, [counter, validateToken]);

  useEffect(() => {
    if (counter > 0) {
      handleValidInterval();
    } else {
      setCounter(RELOAD_RATES);
    }
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [counter, handleValidInterval]);

  return (
    <Dialog
      open={openModal}
      onClose={() => handleLogout()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle showCloseButton onClick={() => handleLogout()}>
        <img
          src={timeout}
          alt="Session timeout"
          className="max-w-[150px] w-full mx-auto my-6"
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="h5" className="text-center font-bold">
          {t('sessionHeader')}
        </Typography>
        <Typography variant="body2" className="text-center !my-4">
          {t('sessionSubHeader')}
        </Typography>
        <Typography variant="body1" className="text-center !my-4">
          {t('sessionExtend')}
        </Typography>
        <div className="flex flex-row flex-wrap gap-5 items-center justify-center my-4">
          <Button
            appearance="primary"
            className="block px-4 py-2"
            onClick={handleResetSession}
          >
            {t('sessionConfirm')}
          </Button>
          <Button
            appearance="secondary"
            className="block px-4 py-2"
            onClick={handleLogout}
          >
            {t('sessionClose')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeout;
